import React from "react"

import { ComplianceGridLegend } from "@/components/compliance"
import { NzDataGrid } from "@/components/nzDataGrid"
import { Page } from "@/components/page"
import { PageCard } from "@/components/page-card"
import { useActiveOrganization } from "@/contexts"
import {
  DEFAULT_PAGE_SIZE,
  DataGridFill,
  useEditRow,
  useInfiniteScroll,
  useSaveGridModel,
} from "@/nzds/data-grid"
import { useDepartmentsSpraypaint } from "@/services/department"
import { useGetBuildingTypes } from "@/services/site"
import { useAvailableReportDates } from "@/services/useAvailableReportDates"
import { useYear } from "@/utils/date/use-year"
import { usePage } from "@/utils/page/use-page"
import type { DateTime } from "luxon"

import { Box } from "@mui/material"
import { useGridApiRef } from "@mui/x-data-grid-pro"
import type { GridSlotProps, GridSlots } from "@mui/x-data-grid-pro"

import type {
  SiteExplorerGridRecord,
  SiteExplorerRecord,
} from "../../services/use-get-site-explorer"
import { useGetSiteExplorer } from "../../services/use-get-site-explorer"
import { useComputedSiteRows } from "../utils"
import { useHandleProcessRowUpdate } from "../utils/use-handle-process-row-update"
import { SitesComplianceGridToolbar } from "./sites-compliance-grid-toolbar"
import {
  defaultColumnVisibilityModel,
  defaultPinnedColumns,
  defaultSortModel,
  useComplianceGridColumns,
} from "./utils/use-compliance-grid-columns/use-compliance-grid-columns"

export const SitesComplianceRoute: React.FC = () => {
  const gridApiRef = useGridApiRef()
  const { activeOrganization } = useActiveOrganization()
  const { availableReportDatesData } = useAvailableReportDates()

  const { buildingTypesData, buildingTypesIsFetching } = useGetBuildingTypes()
  const { departmentsSpraypaintData, departmentsSpraypaintIsFetching } =
    useDepartmentsSpraypaint({
      apiQueryOptions: {
        where: { organization_id: activeOrganization?.id },
      },
    })

  const {
    handleRowEditCancelClick,
    handleRowEditClick,
    handleRowEditSaveClick,
    handleRowEditStop,
    rowModesModel,
  } = useEditRow()
  const defaultColumns = useComplianceGridColumns({
    buildingTypes: buildingTypesData,
    departments: departmentsSpraypaintData,
    handleRowEditCancelClick,
    handleRowEditClick,
    handleRowEditSaveClick,
    rowModesModel,
  })

  const { page } = usePage({
    id: "sitesCompliancePage",
  })

  const { dateRange, setYear } = useYear({
    id: "sitesComplianceYear",
    bounds: availableReportDatesData,
  })

  const handleYearChange = (value: DateTime) => {
    setYear(value.year)
  }

  const {
    gridModel: { columnVisibilityModel, columns, pinnedColumns, sortModel },
    handleColumnOrderChange,
    handleColumnVisibilityModelChange,
    handlePinnedColumnsChange,
    handleSortModelChange,
  } = useSaveGridModel<SiteExplorerGridRecord>({
    gridName: "sitesCompliance",
    gridModel: {
      columns: defaultColumns,
      columnVisibilityModel: defaultColumnVisibilityModel,
      pinnedColumns: defaultPinnedColumns,
      sortModel: defaultSortModel,
    },
  })

  const isSiteExplorerQueryEnabled =
    Boolean(activeOrganization?.id) &&
    Boolean(sortModel[0].field) &&
    Boolean(sortModel[0].sort) &&
    dateRange.isValid

  const {
    siteExplorerData,
    siteExplorerFetchNextPage,
    siteExplorerHasNextPage,
    siteExplorerIsFetching,
    siteExplorerIsFetchingNextPage,
  } = useGetSiteExplorer({
    parameters: {
      query: {
        "filter[organization_id]": activeOrganization?.id,
        "filter[start_date][gte]": dateRange.start?.toISODate(),
        "filter[end_date][lte]": dateRange.end?.toISODate(),
        include_compliance: true,
        "page[size]": DEFAULT_PAGE_SIZE,
        sort: `${sortModel[0].sort === "asc" ? "" : "-"}${sortModel[0].field}`,
        "stats[compliant]": "count",
        "stats[non_compliant]": "count",
        "stats[page]": "count",
        "stats[total_penalties]": "sum",
      },
    },
    initialPage: page,
    queryOptions: {
      enabled: isSiteExplorerQueryEnabled,
    },
  })

  const rows = useComputedSiteRows<SiteExplorerRecord, SiteExplorerGridRecord>({
    data: siteExplorerData,
  })

  const { handleOnRowsScrollEnd } = useInfiniteScroll({
    fetchNextPage: siteExplorerFetchNextPage,
    hasNextPage: siteExplorerHasNextPage,
    isEnabled: isSiteExplorerQueryEnabled,
    isFetchingNextPage: siteExplorerIsFetchingNextPage,
  })

  const { handleProcessRowUpdate } = useHandleProcessRowUpdate(
    departmentsSpraypaintData
  )

  const handleProcessRowUpdateError = React.useCallback((error: Error) => {
    console.error(error)
  }, [])

  return (
    <Page fullHeight>
      <PageCard
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            mb: 3,
          }}
        >
          <ComplianceGridLegend
            compliantCount={siteExplorerData?.pages[0]?.compliant}
            nonCompliantCount={siteExplorerData?.pages[0]?.non_compliant}
            siteCount={siteExplorerData?.pages[0]?.total}
            totalPenalties={siteExplorerData?.pages[0]?.total_penalties}
          />
        </Box>
        <DataGridFill>
          <NzDataGrid
            apiRef={gridApiRef}
            aria-label="Sites compliance"
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            disableColumnFilter
            loading={
              buildingTypesIsFetching ||
              departmentsSpraypaintIsFetching ||
              siteExplorerIsFetching ||
              siteExplorerIsFetchingNextPage
            }
            onColumnOrderChange={handleColumnOrderChange}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            onPinnedColumnsChange={handlePinnedColumnsChange}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            onRowEditStop={handleRowEditStop}
            onRowsScrollEnd={handleOnRowsScrollEnd}
            onSortModelChange={handleSortModelChange}
            pinnedColumns={pinnedColumns}
            processRowUpdate={handleProcessRowUpdate}
            rowModesModel={rowModesModel}
            rows={rows}
            slotProps={{
              basePopper: {
                placement: "bottom-start",
              },
              toolbar: {
                availableDateRange: availableReportDatesData,
                dateRange,
                onYearChange: handleYearChange,
                organizationId: activeOrganization?.id,
              } as GridSlotProps["toolbar"],
            }}
            slots={{
              toolbar: SitesComplianceGridToolbar as GridSlots["toolbar"],
            }}
            sortingMode="server"
            sortModel={sortModel}
          />
        </DataGridFill>
      </PageCard>
    </Page>
  )
}
